import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { InventoryItem } from '../../../redux/inventorySlice';
import { PantryLocation } from '../../../utils/pantryLocation';
import { PantryItemView } from '../../../redux/pantrySelectors';

interface DragState {
  item: InventoryItem;
  x: number;
  y: number;
}

interface UsePantryDragOptions {
  onDrop: (item: InventoryItem, zone: PantryLocation) => void;
  onOpen: (item: InventoryItem) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

interface UsePantryDragReturn {
  drag: DragState | null;
  overZone: PantryLocation | null;
  startDrag: (e: React.PointerEvent<any>, itemView: PantryItemView) => void;
}

const DRAG_THRESHOLD = 5;
const LANDED_FLASH_DURATION = 650;

/**
 * Web-only hook for desktop drag-and-drop between pantry zones.
 * On native, returns inert no-ops (click opens drawer as before).
 */
export function usePantryDrag(
  options: UsePantryDragOptions
): UsePantryDragReturn {
  const isWeb = Platform.OS === 'web';
  const [drag, setDrag] = useState<DragState | null>(null);
  const [overZone, setOverZone] = useState<PantryLocation | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragStartedRef = useRef(false);
  const moveListenerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const upListenerRef = useRef<((e: PointerEvent) => void) | null>(null);

  // Measure container bounds on web
  const containerRef = options.containerRef;
  const containerBoundsRef = useRef<{ left: number; top: number }>({ left: 0, top: 0 });

  // Update container bounds when it mounts
  useEffect(() => {
    if (!isWeb || !containerRef?.current) return;
    const updateBounds = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        containerBoundsRef.current = { left: rect.left, top: rect.top };
      }
    };
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [isWeb, containerRef]);

  // Clean up listeners on unmount
  useEffect(() => {
    return () => {
      if (moveListenerRef.current) {
        window.removeEventListener('pointermove', moveListenerRef.current);
      }
      if (upListenerRef.current) {
        window.removeEventListener('pointerup', upListenerRef.current);
      }
    };
  }, []);

  if (!isWeb) {
    // Native: return inert hook that opens drawer on press
    return {
      drag: null,
      overZone: null,
      startDrag: (e, itemView) => {
        options.onOpen(itemView.item);
      },
    };
  }

  const zoneAt = (ev: PointerEvent): PantryLocation | null => {
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    const zoneEl = el?.closest('[data-zone]') as HTMLElement | null;
    return zoneEl ? (zoneEl.getAttribute('data-zone') as PantryLocation) : null;
  };

  const startDrag = (e: React.PointerEvent<any>, itemView: PantryItemView) => {
    if (e.button !== 0) return; // Only left-click

    const sx = e.clientX;
    const sy = e.clientY;
    dragStartRef.current = { x: sx, y: sy };
    dragStartedRef.current = false;

    const move = (ev: PointerEvent) => {
      if (!dragStartedRef.current) {
        const dx = ev.clientX - sx;
        const dy = ev.clientY - sy;
        if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
          dragStartedRef.current = true;
        }
      }

      if (dragStartedRef.current) {
        // Convert clientX/Y to coordinates relative to container
        const x = ev.clientX - containerBoundsRef.current.left;
        const y = ev.clientY - containerBoundsRef.current.top;
        setDrag({ item: itemView.item, x, y });
        setOverZone(zoneAt(ev));
      }
    };

    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      moveListenerRef.current = null;
      upListenerRef.current = null;

      if (dragStartedRef.current) {
        const z = zoneAt(ev);
        if (z && z !== itemView.item.location) {
          options.onDrop(itemView.item, z);
        }
      } else {
        // Plain click (< 5px movement) → open drawer
        options.onOpen(itemView.item);
      }

      setDrag(null);
      setOverZone(null);
      dragStartRef.current = null;
      dragStartedRef.current = false;
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    moveListenerRef.current = move;
    upListenerRef.current = up;
  };

  return {
    drag,
    overZone,
    startDrag,
  };
}
